// frontend/src/hooks/use-active-branch.ts

import { useQuery } from "@tanstack/react-query";
import { useEffect, useSyncExternalStore } from "react";
import { branchesQueries } from "@/modules/branches/queries";
import { useSession } from "@/providers/session-provider";

export const CURRENT_BRANCH_STORAGE_KEY = "current_branch";

export function getActiveBranchId(): string {
	return localStorage.getItem(CURRENT_BRANCH_STORAGE_KEY) ?? "";
}

function setActiveBranchId(newValue: string): void {
	localStorage.setItem(CURRENT_BRANCH_STORAGE_KEY, newValue);
	window.dispatchEvent(
		new StorageEvent("storage", {
			key: CURRENT_BRANCH_STORAGE_KEY,
			newValue,
		})
	);
}

const getSnapshot = () => localStorage.getItem(CURRENT_BRANCH_STORAGE_KEY) ?? "";

const subscribe = (listener: () => void) => {
	window.addEventListener("storage", listener);
	return () => window.removeEventListener("storage", listener);
};

export function useActiveBranch() {
	const session = useSession();
	const localBranchId = useSyncExternalStore(subscribe, getSnapshot);

	const branches = useQuery({
		...branchesQueries.fullList(),
		enabled: !!session?.id,
	});
	const defaultBranchId = branches.data?.[0]?.id ?? "";

	// Check if the stored branch ID actually exists in the current branch list
	const isStoredBranchValid = !!localBranchId && !!branches.data?.some((b) => b.id === localBranchId);

	useEffect(() => {
		if (branches.isLoading || branches.isFetching) return;
		if (!branches.data?.length) return;
		if (isStoredBranchValid) return;

		// Stored branch is empty or stale — reset to the first available branch
		setActiveBranchId(defaultBranchId);
	}, [branches.isLoading, branches.data, isStoredBranchValid, defaultBranchId, branches.isFetching]);

	return {
		activeBranchId: isStoredBranchValid ? localBranchId : defaultBranchId,
		setActiveBranch: setActiveBranchId,
	};
}
